import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SITE = 'https://www.kynaixsports.com';

// index.html ships a complete <head> describing the HOMEPAGE — canonical,
// description, robots, og:* and twitter:* are all hard-coded to "/". That is
// correct before any JS runs, and wrong on every other route: it told Google
// that /marketplace, /help and /sport/* were duplicates of the homepage, so
// none of them could be indexed on their own.
//
// The obvious fix is <Helmet>, and it is a trap here. Helmet APPENDS the tags
// it manages and never removes the static ones already in the document, so a
// page that renders its own canonical through Helmet ends up serving two
// conflicting canonicals. Google treats that as ambiguous and may discard both
// — strictly worse than one wrong canonical. That is why the existing Helmet
// canonical on SportPage never helped.
//
// So this component REWRITES the tags that already exist instead of adding to
// them. Exactly one of each tag is present at all times, the raw HTML stays
// correct for the homepage, and every other route is corrected as soon as the
// app mounts.
const setMeta = (selector, attr, value, create) => {
    let el = document.head.querySelector(selector);
    if (!el && create) {
        el = document.createElement(create.tag);
        Object.entries(create.attrs).forEach(([k, v]) => el.setAttribute(k, v));
        document.head.appendChild(el);
    }
    if (el) el.setAttribute(attr, value);
};

/**
 * Per-route <head> management.
 *
 * @param {string}  title        Full <title>. Falls back to the document default.
 * @param {string}  description  Meta description, ~150-160 chars.
 * @param {string}  image        Absolute URL for social cards.
 * @param {boolean} noindex      Keep this route out of search results.
 * @param {string}  path         Override the canonical path (defaults to the
 *                               current location, query string deliberately
 *                               dropped so ?tab=… does not fork the canonical).
 */
export default function Seo({ title, description, image, noindex = false, path, ogTitle, ogDescription }) {
    const location = useLocation();
    const url = `${SITE}${path ?? location.pathname}`.replace(/\/+$/, '') || SITE;

    useEffect(() => {
        // A search result and a shared link want different copy: the title is
        // written for ranking, the social card for a human deciding whether to
        // tap. ogTitle/ogDescription let a page keep the punchier version rather
        // than have the search-tuned string overwrite it.
        const social = ogTitle ?? title;
        const socialDesc = ogDescription ?? description;

        if (title) document.title = title;
        if (social) {
            setMeta('meta[property="og:title"]', 'content', social);
            setMeta('meta[name="twitter:title"]', 'content', social);
        }
        if (description) setMeta('meta[name="description"]', 'content', description);
        if (socialDesc) {
            setMeta('meta[property="og:description"]', 'content', socialDesc);
            setMeta('meta[name="twitter:description"]', 'content', socialDesc);
        }
        if (image) {
            setMeta('meta[property="og:image"]', 'content', image);
            setMeta('meta[name="twitter:image"]', 'content', image);
        }

        // The canonical is the whole point of this component. Create it if the
        // template ever loses it, so a route can never end up without one.
        setMeta('link[rel="canonical"]', 'href', url, { tag: 'link', attrs: { rel: 'canonical' } });
        setMeta('meta[property="og:url"]', 'content', url);

        // A soft 404 — 200 OK on a page that does not exist — is the other half
        // of the problem: the SPA answers 200 for every unknown path, so a
        // crawler sees unlimited "valid" pages. Serving a real 404 status needs
        // the edge, but noindex keeps them out of the index either way, and is
        // what Google documents for exactly this case.
        setMeta(
            'meta[name="robots"]',
            'content',
            noindex ? 'noindex, follow' : 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1',
            { tag: 'meta', attrs: { name: 'robots' } },
        );
        setMeta(
            'meta[name="googlebot"]',
            'content',
            noindex ? 'noindex, follow' : 'index, follow',
            { tag: 'meta', attrs: { name: 'googlebot' } },
        );
    }, [title, description, image, noindex, url, ogTitle, ogDescription]);

    return null;
}
